import { useEffect, useRef, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Ignore movements smaller than this. A trackpad flick, a rubber-band bounce or
// a mobile address bar collapsing all fire scroll events worth a few pixels,
// and reacting to those makes the bar flicker.
const DELTA_THRESHOLD = 6;

// Leave the bar alone over the first stretch of the page, so it never slides
// away while the hero is still the thing being looked at.
const HIDE_AFTER = 140;

// Below this the page counts as "at the top", where the bar rests under the
// promo banner instead of floating over content.
const TOP_THRESHOLD = 8;

const currentScrollY = () => Math.max(window.scrollY || 0, 0);

// Sliding chrome is exactly the motion this setting asks us to drop, so track
// it live rather than reading it once at mount.
const useReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.(REDUCED_MOTION_QUERY).matches === true
  );

  useEffect(() => {
    const query = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (!query) {
      return undefined;
    }

    const sync = () => setReduced(query.matches);
    sync();
    query.addEventListener('change', sync);

    return () => query.removeEventListener('change', sync);
  }, []);

  return reduced;
};

/**
 * Drives an auto-hiding header.
 *
 * `hidden` turns true while the user scrolls down away from the top of the
 * page and flips back the moment they scroll up, so the bar is always one
 * gesture away without occupying the screen the rest of the time. `atTop`
 * reports whether the page has moved at all, which lets the bar tell apart
 * resting in place from floating over content.
 *
 * Pass `locked` to pin the bar open - while the mobile menu is expanded, for
 * instance - so it cannot slide out from under whatever hangs off it.
 */
const useAutoHideHeader = ({ locked = false } = {}) => {
  const [hidden, setHidden] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const lastScrollY = useRef(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    // Whether the bar may slide away. `atTop` is tracked either way - it only
    // drives a shadow, which is not motion anyone asked us to drop.
    const canHide = !reducedMotion && !locked;
    if (!canHide) {
      setHidden(false);
    }

    let frame = null;

    const update = () => {
      frame = null;

      const scrollY = currentScrollY();
      const delta = scrollY - lastScrollY.current;

      setAtTop(scrollY <= TOP_THRESHOLD);

      if (!canHide) {
        lastScrollY.current = scrollY;
        return;
      }

      // Hold the last position until the page has moved a meaningful amount,
      // so slow scrolling still adds up to a direction change.
      if (Math.abs(delta) < DELTA_THRESHOLD) {
        return;
      }

      setHidden(delta > 0 && scrollY > HIDE_AFTER);
      lastScrollY.current = scrollY;
    };

    const handleScroll = () => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(update);
    };

    // Reloading part-way down a page starts us mid-scroll, so measure once
    // before listening rather than assuming we begin at the top.
    lastScrollY.current = currentScrollY();
    update();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [locked, reducedMotion]);

  return { hidden, atTop };
};

export default useAutoHideHeader;
